"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
// import { Link } from "@/components/common/Link"; // 如果導航列還需要用到則保留
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
export default function ProjectHero() {
  const {
    ref: headerRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });

  // 1. 新增：捲動到下方實績網格的函式
  const scrollToProjects = (e: React.MouseEvent) => {
    e.preventDefault();
    // 尋找你下方區塊中定義的 ID (project-section-top 是標題位置，project-showcase 是整個容器)
    const element = document.getElementById("project-section-top");
    if (element) {
      const offset = 100; // 偏移量，根據你的導覽列高度調整
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // 之前的 scrollToService 保持不變...
  const scrollToService = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById("服務流程-service-process");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };
  return <section ref={headerRef} data-section-id="154387" id="project-hero" className="relative h-[450px] md:h-[500px] w-full overflow-hidden bg-[#f3ede5] flex items-center">
  {/* 背景圖片層：展現柔和自然的樹影與光感 */}
  <div className="absolute inset-0 z-0">
    <img src="https://hent.services/wp-content/uploads/2026/05/PROJECT-SERVICES-scaled.webp" alt="Hent's Flooring Project Showcase" className={cn("h-full w-full object-cover transition-transform duration-[3000ms] ease-out opacity-90 object-center", isVisible ? "scale-102" : "scale-100")} />
  
    <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent z-10" />
  </div>

  <div className="container relative z-30 mx-auto px-6 md:px-12 lg:px-20">
    <div className={cn("max-w-3xl transition-all duration-1000 delay-300", isVisible ? "translate-x-0 opacity-100" : "-translate-x-6 opacity-0")}>
      
      {/* 1. 麵包屑導覽：改為優雅細緻的石磚灰，降低視覺權重 */}
      <nav className="mb-8 flex items-center gap-2 text-1xl md:text-xl font-sans tracking-[0.2em] text-[#3e3126]/60">
        <Link to="/" className="transition-colors hover:text-[#3e3126]">HOME</Link>
        <span className="opacity-40">/</span>
        <span className="text-[#3e3126] font-medium">PROJECT & SERVICES</span>
      </nav>

      {/* 2. 主標題區塊（依據圖片規範 100% 還原） */}
      <div className="flex flex-col items-start">
        {/*  核心修改 4：主標題
            - 移除 font-serif 和 text-5xl/6xl，改用 font-hk-regular 和更內斂的 text-3xl md:text-4xl
            - 顏色精準套用 R62 G49 B38 (#3e3126)
            - 徹底拿掉大塊的文字陰影 drop-shadow
         */}
        <h1 className="font-hk-regular text-2xl md:text-4xl font-medium tracking-widest text-[#3e3126] leading-tight">
          回歸自然本質
        </h1>
        
        {/*  核心修改 5：中文副標題
            - 字體：font-hk-light，顏色：#3e3126
            - 移除原本獨立的巨大文字段落，直接咬合在主標題下方，建立精品名牌感
         */}
        <p className="mt-3 text-1xl md:text-xl font-hk-light tracking-widest text-[#3e3126]/90 flex items-center gap-2 flex-wrap">
          每一種生活 <span>|</span> 都有適合它的空間溫度
        </p>
      </div>

      {/* 3. 工藝裝飾細線 */}
      {/*  核心修改 6：拔除原本帶有發光特效的黃金粗線，換成寬度 40px、厚度 1px 的內斂深褐品牌色細線 */}
      <div className="h-[1px] w-10 bg-[#3e3126]/30 my-6" />

      {/* 4. 英文副標題區塊（依據圖片規範 100% 還原） */}
      {/*  核心修改 7：
          - 字體：Bahnschrift (Light)，顏色：純白 #ffffff
          - tracking-[0.25em]：大膽拉開字距，重現經典北歐現代雜誌的封底落款質感
       */}
      <div className="font-sans text-1xl md:text-xl font-light tracking-[0.25em] text-white uppercase select-none">
        FEELS LIKE HOME
      </div>

      {/* 5. 互動按鈕區塊：改為低調內斂的人文互動樣式 */}
      {/*  核心修改 8：
          - 探索工程案例按鈕：移除發光背景，改用與 Footer 一致的直角純白底/半透明質感，或者與標題呼應的優雅直角
       */}
      <div className="mt-10 flex flex-wrap gap-6 items-center">
        <Button onClick={scrollToProjects} size="lg" className="h-11 rounded-none px-8 text-xs tracking-widest transition-all bg-[#604f45] hover:bg-[#4a3d35] text-white border-none shadow-sm cursor-pointer">
          <span className="flex items-center gap-2">
            探索工程案例 
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </span>
        </Button>
        
        <button onClick={scrollToService} className="group flex items-center gap-2 text-xs font-medium tracking-widest text-[#3e3126]/80 transition-colors hover:text-[#3e3126]">
          了解服務流程
          <div className="h-[1px] w-0 bg-[#3e3126] transition-all group-hover:w-6" />
        </button>
      </div>

    </div>
  </div>
</section>;
}